import React, { useEffect, useState } from 'react'
import { useParams, Navigate, useLocation, Link } from 'react-router-dom'
import { Box, Paper, Stack, Typography } from '@mui/material'
import anearApi from '../Api/AnearApi'
import merchantOpsApi from '../Api/MerchantOpsApi'
import TokenStorage from '../Api/TokenStorage'
import UIStateContainer from '../Containers/UIStateContainer'
import ROUTES from '../../routesConfig'
import AuthTokenExpiredError from '../Api/AuthTokenExpiredError'
import useNavigation from '../Utils/navigationUtils'
import UserAuthContainer from '../Containers/UserAuthContainer'
import AnearVenue from '../Models/AnearVenue'
import {
  contentColumnMaxWidthSx,
  contentColumnPageShellSx
} from '../UI/contentColumn'
import PageBackRow from '../UI/PageBackRow'

const humanizeSlug = (value = '') => String(value)
  .replace(/[-_]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase())

const displayVenueTitle = (venue) => {
  const fallback = String(venue?.name || venue?.slug || '').trim()
  if (!fallback) return 'Venue'
  return /^[-a-z0-9_]+$/i.test(fallback) ? humanizeSlug(fallback) : fallback
}

const venueRolePillPalette = [
  { color: '#082F49', bgcolor: '#7DD3FC', borderColor: 'rgba(125, 211, 252, 0.45)' },
  { color: '#3B0764', bgcolor: '#D8B4FE', borderColor: 'rgba(216, 180, 254, 0.45)' },
  { color: '#052E16', bgcolor: '#86EFAC', borderColor: 'rgba(134, 239, 172, 0.45)' },
  { color: '#431407', bgcolor: '#FDBA74', borderColor: 'rgba(253, 186, 116, 0.45)' },
  { color: '#4A044E', bgcolor: '#F5A9F7', borderColor: 'rgba(245, 169, 247, 0.45)' }
]

const venueRolePillColors = (position) => {
  const index = Math.max(0, Number(position || 0))
  return venueRolePillPalette[index % venueRolePillPalette.length]
}

const assignmentEventHostRoles = (assignment) => {
  const raw = assignment?.['event-host-roles'] ?? assignment?.event_host_roles
  if (!Array.isArray(raw)) return []
  return raw
    .map((role) => ({
      id: role?.id || '',
      name: role?.name || '',
      slug: String(role?.slug || '').toLowerCase(),
      position: Number.isFinite(role?.position) ? role.position : Number(role?.position || 0)
    }))
    .filter((role) => role.name)
}

const VenuePage = () => {
  const tokenStorage = new TokenStorage()
  const { venueSlug } = useParams()
  const location = useLocation()
  const merchantPreviewVenueId = location.state?.previewMode === 'merchant'
    ? String(location.state?.venueId || '').trim()
    : ''
  const isMerchantPreviewMode = location.state?.previewMode === 'merchant' && Boolean(merchantPreviewVenueId)
  const [venue, setVenue] = useState(null)
  const [loading, setLoading] = useState(true)
  const uiContainer = UIStateContainer.useContainer()
  const userAuthContainer = UserAuthContainer.useContainer()
  const { startProgress, progressComplete } = uiContainer
  const { handleAuthTokenExpired } = useNavigation()

  const computeVenueHomeContextExpiry = (loadedVenue) => {
    const scheduledAtMs = Date.parse(loadedVenue?.scheduledAt)
    const endsAtMs = Date.parse(loadedVenue?.endsAt || loadedVenue?.ends_at)
    const oneHourMs = 60 * 60 * 1000
    const fallbackMs = 2 * 60 * 60 * 1000

    if (Number.isFinite(endsAtMs)) {
      return endsAtMs + oneHourMs
    }

    if (Number.isFinite(scheduledAtMs)) {
      return scheduledAtMs + fallbackMs
    }

    return Date.now() + fallbackMs
  }

  useEffect(() => {
    let cancelled = false

    const fetchVenue = async () => {
      try {
        startProgress()
        const result = isMerchantPreviewMode
          ? await merchantOpsApi.showVenue(merchantPreviewVenueId).then(
            (json) => new AnearVenue(json.data, json.included)
          )
          : await anearApi.getVenue(venueSlug)
        if (!cancelled) {
          setVenue(result)
          if (!isMerchantPreviewMode) {
            tokenStorage.setVenueHomeContext({
              venueSlug: result?.slug || venueSlug,
              expiresAt: computeVenueHomeContextExpiry(result),
              source: 'venue_page',
            })
          }
        }
      } catch (err) {
        if (!cancelled) {
          if (!isMerchantPreviewMode) {
            // Prevent EventsPage <-> VenuePage redirect loops on stale venue context.
            tokenStorage.clearVenueHomeContext()
          }
          if (err instanceof AuthTokenExpiredError) {
            handleAuthTokenExpired(err, userAuthContainer)
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
        progressComplete()
      }
    }

    fetchVenue()
    return () => { cancelled = true }
  }, [isMerchantPreviewMode, merchantPreviewVenueId, venueSlug]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return null

  if (!venue) {
    return <Navigate to={ROUTES.EVENTS} replace />
  }

  const merchantPreviewReturnTo = location.state?.previewMode === 'merchant'
    ? (location.state?.from || ROUTES.MERCHANT_DASHBOARD)
    : null
  const venueTitle = displayVenueTitle(venue)
  const venueCoverPhotoUrl = venue.venuePhotoMediumUrl || venue.venuePhotoUrl

  const playableEvents = venue.events()
    .filter((event) => String(event?.state || '').toLowerCase() !== 'canceled')
    .map(event => ({
    id: event.id,
    playableState: event.isCreator?.() ? 'creator' : 'open_house',
    anearEvent: event
  }))
  const hasLiveVenueEvent = playableEvents.some(
    ({ anearEvent }) => String(anearEvent?.state || '').toLowerCase() === 'live'
  )
  const hostAssignments = (venue.venueZoneAssignments?.() || [])
    .filter((assignment) => assignment?.['host-access'])
  const hostZoneIdSet = new Set(
    hostAssignments
      .map((a) => String(a['zone-id'] || '').trim())
      .filter(Boolean)
  )
  const userHasDelegatedHostAccess = hostZoneIdSet.size > 0

  const eventParticipationPredicate = (anearEvent) => {
    const isParticipant = typeof anearEvent.isParticipant === 'function' && anearEvent.isParticipant()
    const isCreator = typeof anearEvent.isCreator === 'function' && anearEvent.isCreator()
    const isParticible = typeof anearEvent.isParticible === 'function' && anearEvent.isParticible()
    const state = String(anearEvent?.state || '').toLowerCase()
    const openStates = ['announce', 'live', 'paused']
    if (isParticipant || isCreator) return true
    if (openStates.includes(state) && isParticible) return true
    return false
  }

  const filteredVenuePlayables = playableEvents.filter(({ anearEvent }) => {
    const zid = String(anearEvent?.zone?.id || '').trim()
    if (userHasDelegatedHostAccess && hostZoneIdSet.has(zid)) {
      return false
    }
    if (!userHasDelegatedHostAccess) return true
    return eventParticipationPredicate(anearEvent)
  })

  const activeEventByZoneId = playableEvents.reduce((memo, playable) => {
    const zoneId = playable?.anearEvent?.zone?.id
    if (zoneId && !memo[zoneId]) memo[zoneId] = playable.anearEvent
    return memo
  }, {})

  return (
    <Box sx={contentColumnPageShellSx}>
      <Box sx={{ ...contentColumnMaxWidthSx, overflowX: 'hidden' }}>
        <Paper
          sx={{
            position: 'relative',
            ...(hasLiveVenueEvent
              ? {
                  px: { xs: 1.25, sm: 1.6 },
                  pt: { xs: 0.75, sm: 1 },
                  pb: { xs: 1.25, sm: 1.6 }
                }
              : { p: { xs: 1.25, sm: 1.6 } }),
            borderRadius: 3,
            bgcolor: '#0B0F19',
            color: '#E6E9EF',
            border: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden'
          }}
        >
          {isMerchantPreviewMode ? (
            <Box
              sx={{
                position: 'absolute',
                top: '1rem',
                right: '-1.95rem',
                zIndex: 3,
                width: '8.2rem',
                transform: 'rotate(40deg)',
                textAlign: 'center',
                fontSize: '0.72rem',
                letterSpacing: '0.1em',
                fontWeight: 700,
                color: '#2C1F05',
                background: 'linear-gradient(180deg, #F7E7B0 0%, #D8B969 100%)',
                border: '1px solid rgba(122, 91, 22, 0.7)',
                boxShadow: '0 0.3rem 0.5rem rgba(9, 10, 18, 0.4)',
                py: 0.22,
                lineHeight: 1
              }}
            >
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  p: 0,
                  m: -1,
                  overflow: 'hidden',
                  clip: 'rect(0 0 0 0)',
                  whiteSpace: 'nowrap',
                  border: 0
                }}
              >
                Merchant Preview
              </Box>
              PREVIEW
            </Box>
          ) : null}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: hasLiveVenueEvent ? 0.95 : 1.15 }}>
            {merchantPreviewReturnTo ? (
              <PageBackRow to={merchantPreviewReturnTo} label="< Exit" />
            ) : null}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                  color: '#F8FAFC',
                  fontWeight: 700,
                  fontSize: { xs: '1.72rem', sm: '2rem' },
                  lineHeight: 1.08,
                  fontStyle: 'normal',
                  letterSpacing: '-0.02em',
                  fontFamily: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
                  textShadow: '0 2px 12px rgba(0,0,0,0.35)',
                  textWrap: 'balance',
                  textAlign: 'center',
                  maxWidth: '24rem'
                }}
              >
                {venueTitle}
              </Typography>
            </Box>

            {venueCoverPhotoUrl ? (
              <Box
                sx={{
                  width: '100%',
                  p: '0.16rem',
                  borderRadius: '1rem',
                  background: 'linear-gradient(180deg, rgba(242, 213, 142, 0.96), rgba(172, 117, 35, 0.96))',
                  boxShadow:
                    '0 0 0.16rem rgba(255, 230, 179, 0.95), 0 0 1.1rem rgba(212, 158, 63, 0.34), 0 0 2rem rgba(96, 61, 11, 0.18)'
                }}
              >
                <Box
                  component="img"
                  src={venueCoverPhotoUrl}
                  alt={venueTitle}
                  sx={{
                    width: '100%',
                    aspectRatio: '16 / 10',
                    objectFit: 'cover',
                    objectPosition: 'center 28%',
                    display: 'block',
                    borderRadius: '0.84rem',
                    border: '1px solid rgba(255, 244, 214, 0.58)',
                    bgcolor: '#D5D8E2'
                  }}
                />
              </Box>
            ) : null}

          {filteredVenuePlayables.length > 0 ? (
            <Typography
              component="p"
              sx={{
                m: 0,
                color: '#F8FAFC',
                fontWeight: 700,
                fontSize: '1.05rem',
                lineHeight: 1.3,
                textAlign: 'center'
              }}
            >
              Tap one to join in.
            </Typography>
          ) : null}

          {hostAssignments.length > 0 ? (
            <>
              <Typography
                variant="h6"
                sx={{
                  padding: '0.2rem 0 0.6rem',
                  fontWeight: 700,
                  color: 'rgba(230,233,239,0.84)',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                Your Assigned Apps
              </Typography>
              <Stack spacing={1.2} sx={{ mb: 2 }}>
                {hostAssignments.map((assignment) => {
                  const zoneId = assignment['zone-id']
                  const appShortName = assignment['app-short-name'] || 'App'
                  const appLongName = assignment['app-long-name'] || ''
                  const appIconUrl = assignment['app-icon-url'] || ''
                  const linkedEvent = zoneId ? activeEventByZoneId[zoneId] : null
                  const viewerIsMerchant = typeof userAuthContainer.isMerchant === 'function'
                    && userAuthContainer.isMerchant()
                  // Non-merchant invited hosts can only enter their assigned app once the
                  // merchant has created the event for that zone. Until then the tile
                  // shows a non-navigable "Waiting for merchant" status instead of a
                  // "Create Event" CTA that would 403 against the API.
                  const canCreateForZone = viewerIsMerchant
                  const isNavigable = Boolean(linkedEvent) || canCreateForZone
                  const targetPath = linkedEvent
                    ? ROUTES.EVENT_PAGE(linkedEvent.slug)
                    : (canCreateForZone ? ROUTES.CREATE_EVENT(zoneId) : null)
                  const targetLabel = linkedEvent
                    ? 'Open Event'
                    : (canCreateForZone ? 'Create Event' : 'Waiting for merchant')

                  const tileBaseProps = isNavigable
                    ? {
                        component: Link,
                        to: targetPath,
                        state: { from: `${location.pathname}${location.search || ''}` }
                      }
                    : {
                        component: 'div',
                        'aria-label': `${appShortName} is waiting for the merchant to create this event`
                      }

                  return (
                    <Box
                      key={assignment.id || `${zoneId}-${appShortName}`}
                      {...tileBaseProps}
                      sx={{ textDecoration: 'none' }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.08)',
                          borderRadius: 2,
                          px: 1.1,
                          py: 1.05,
                          display: 'flex',
                          alignItems: 'stretch',
                          justifyContent: 'space-between',
                          gap: 1,
                          border: '1px solid rgba(255,255,255,0.1)',
                          transition: 'background 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.12)',
                            borderColor: 'rgba(255,255,255,0.16)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.35)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, minWidth: 0, flex: 1 }}>
                          <Box
                            sx={{
                              width: '4.6rem',
                              borderRadius: 1.35,
                              overflow: 'hidden',
                              flexShrink: 0,
                              bgcolor: 'rgba(255,255,255,0.08)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {appIconUrl ? (
                              <Box
                                component="img"
                                src={appIconUrl}
                                alt={appShortName}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <Typography sx={{ color: '#E6E9EF', fontWeight: 800, fontSize: '1.2rem' }}>
                                {String(appShortName).charAt(0)}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Typography sx={{ color: '#F8FAFC', fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>
                              {appShortName}
                            </Typography>
                            {appLongName ? (
                              <Typography sx={{ color: 'rgba(230,233,239,0.72)', fontSize: '0.86rem', lineHeight: 1.38, mt: 0.18 }}>
                                {appLongName}
                              </Typography>
                            ) : null}
                            {(() => {
                              const hostRoles = assignmentEventHostRoles(assignment)
                              const pills = hostRoles.length > 0
                                ? hostRoles
                                : [{ id: 'implicit-host', name: 'Host', slug: 'host', position: 0 }]
                              return (
                                <Stack direction="row" spacing={0.45} sx={{ mt: 0.55, flexWrap: 'wrap' }}>
                                  {pills.map((role) => {
                                    const roleColors = venueRolePillColors(role.position)
                                    return (
                                      <Box
                                        key={`${assignment.id || zoneId}-${role.id || role.slug}`}
                                        aria-label={`Your role ${role.name}`}
                                        sx={{
                                          px: 0.9,
                                          py: 0.22,
                                          borderRadius: 999,
                                          fontWeight: 800,
                                          fontSize: '0.7rem',
                                          letterSpacing: '0.02em',
                                          color: roleColors.color,
                                          bgcolor: roleColors.bgcolor,
                                          border: `1px solid ${roleColors.borderColor}`
                                        }}
                                      >
                                        {role.name}
                                      </Box>
                                    )
                                  })}
                                </Stack>
                              )
                            })()}
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            flexShrink: 0,
                            alignSelf: 'center',
                            px: 1.15,
                            py: 0.6,
                            borderRadius: 999,
                            fontWeight: 800,
                            fontSize: isNavigable ? '0.92rem' : '0.78rem',
                            letterSpacing: isNavigable ? 'normal' : '0.02em',
                            textTransform: isNavigable ? 'none' : 'uppercase',
                            ...(isNavigable
                              ? {
                                  color: '#052E16',
                                  background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
                                }
                              : {
                                  color: 'rgba(230,233,239,0.78)',
                                  bgcolor: 'rgba(255,255,255,0.06)',
                                  border: '1px solid rgba(255,255,255,0.14)'
                                })
                          }}
                        >
                          {targetLabel}
                        </Box>
                      </Paper>
                    </Box>
                  )
                })}
              </Stack>
            </>
          ) : null}

          {filteredVenuePlayables.length > 0 ? (
            <Stack spacing={1.2}>
              {filteredVenuePlayables.map(({ anearEvent }) => {
                const appAttributes = anearEvent?.zone?.app?.attributes || {}
                const appIconUrl = appAttributes['icon-url'] || ''
                const appShortName = appAttributes['short-name'] || anearEvent?.name || 'App'
                const appLongName =
                  appAttributes['long-name'] ||
                  appAttributes['app-long-name'] ||
                  ''

                return (
                  <Box
                    key={anearEvent.id}
                    component={Link}
                    to={ROUTES.EVENT_PAGE(anearEvent.slug)}
                    state={{ from: `${location.pathname}${location.search || ''}` }}
                    sx={{ textDecoration: 'none' }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.08)',
                        borderRadius: 2,
                        px: 1.1,
                        py: 1.05,
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'space-between',
                        gap: 1,
                        border: '1px solid rgba(255,255,255,0.1)',
                        transition: 'background 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.12)',
                          borderColor: 'rgba(255,255,255,0.16)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.35)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            width: '4.6rem',
                            borderRadius: 1.35,
                            overflow: 'hidden',
                            flexShrink: 0,
                            bgcolor: 'rgba(255,255,255,0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {appIconUrl ? (
                            <Box
                              component="img"
                              src={appIconUrl}
                              alt={appShortName}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Typography sx={{ color: '#E6E9EF', fontWeight: 800, fontSize: '1.2rem' }}>
                              {String(appShortName).charAt(0)}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Typography sx={{ color: '#F8FAFC', fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>
                            {appShortName}
                          </Typography>
                          {appLongName ? (
                            <Typography sx={{ color: 'rgba(230,233,239,0.72)', fontSize: '0.86rem', lineHeight: 1.38, mt: 0.18 }}>
                              {appLongName}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          flexShrink: 0,
                          alignSelf: 'center',
                          px: 1.15,
                          py: 0.6,
                          borderRadius: 999,
                          color: '#052E16',
                          fontWeight: 800,
                          fontSize: '0.92rem',
                          background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                          boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
                        }}
                      >
                        Join
                      </Box>
                    </Paper>
                  </Box>
                )
              })}
            </Stack>
          ) : (
            <Typography sx={{ color: 'rgba(230,233,239,0.72)', py: 1 }}>
              {userHasDelegatedHostAccess
                ? 'No other joinable events at this venue right now. Use Your Assigned Apps above for apps you help host.'
                : 'No ongoing events at this venue right now.'}
            </Typography>
          )}
          </Box>
        </Paper>
        </Box>
      </Box>
  )
}

export default VenuePage
